import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';

import type { IResourceDatabaseViewItemsData } from '@/api/types';
import type { ISelectedFolder } from '@/store/types';
import type { RootState } from '@/store';

import {
  ResourceDatabaseItemsContainer,
  ResourceDatabaseItemsViewHeadSection,
  ResourcesBreadcrumb,
  ResourcesSimpleTableContainer,
} from '@/components/common';
import { ResourceModalType } from '@/hooks/enums';
import { ResourceDatabaseTitleAction } from '../ResourceDatabaseTitleAction';
import { selectFoldersList } from '@/store/selectors/resourcesSelectors';
import {
  combineItemsToShow,
  combineSelectedView,
  combineSelectedViewAccessType,
} from '@/store/combiners/resourcesCombiners';
import { getSelectedFolderName } from '@/helpers';
import { useAppDispatch, useAppSelector, useDatabaseItemModal, useResourceNavigation } from '@/hooks';
import { getDatabaseFieldsList, getDatabaseFieldTypes, getDatabaseViewsList } from '@/store/slices/resourcesSlice';

const resourceViewRouteSelector = createSelector(
  selectFoldersList,
  (_: RootState, selectedFolder: ISelectedFolder) => selectedFolder,
  (foldersList, selectedFolder) => ({
    foldersList,
    itemsToShow: combineItemsToShow(foldersList, selectedFolder),
    viewAccessType: combineSelectedViewAccessType(foldersList, selectedFolder),
    selectedView: combineSelectedView(foldersList, selectedFolder),
  }),
);

const ResourceViewRoute = (): React.ReactElement => {
  const { t } = useTranslation();
  const { selectedFolder } = useResourceNavigation();
  const { foldersList, itemsToShow, selectedView, viewAccessType } = useAppSelector(state =>
    resourceViewRouteSelector(state, selectedFolder),
  );
  const { handleOpenModal: handleOpenDatabaseItemModal } = useDatabaseItemModal();
  const dispatch = useAppDispatch();
  const displayedDbIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!selectedView) {
      const getDatabaseViewsListRequest = dispatch(
        getDatabaseViewsList({
          clientId: selectedFolder.clientId,
          dbId: selectedFolder.folderId,
        }),
      );

      return () => {
        getDatabaseViewsListRequest.abort();
      };
    }
    return;
  }, [dispatch, selectedFolder.clientId, selectedFolder.folderId, selectedFolder.viewId, selectedView]);

  useEffect(() => {
    if (selectedFolder.folderId !== displayedDbIdRef.current) {
      const getDatabaseFieldsListRequest = dispatch(
        getDatabaseFieldsList({
          clientId: selectedFolder.clientId,
          dbId: selectedFolder.folderId,
        }),
      );

      displayedDbIdRef.current = selectedFolder.folderId;
      return () => getDatabaseFieldsListRequest.abort();
    }
    return;
  }, [dispatch, selectedFolder.clientId, selectedFolder.folderId]);

  useEffect(() => {
    const getDatabaseFieldTypesRequest = dispatch(getDatabaseFieldTypes());

    return () => getDatabaseFieldTypesRequest.abort();
  }, [dispatch]);

  if (!selectedView) return <></>;

  return (
    <>
      <ResourcesBreadcrumb />
      <ResourcesSimpleTableContainer
        tableHeadSectionSlot={
          <ResourceDatabaseItemsViewHeadSection
            itemCount={(itemsToShow as IResourceDatabaseViewItemsData).RecordCount ?? 0}
            addButtonCallback={() =>
              handleOpenDatabaseItemModal({
                modalType: ResourceModalType.new,
                data: {
                  clientId: selectedFolder.clientId,
                  databaseId: selectedFolder.folderId,
                  view: selectedView,
                },
              })
            }
          />
        }
        tableComponentSlot={
          <ResourceDatabaseItemsContainer
            items={itemsToShow as IResourceDatabaseViewItemsData}
            view={selectedView}
            clientId={selectedFolder.clientId}
            openEditItemModal={item =>
              handleOpenDatabaseItemModal({
                modalType: ResourceModalType.edit,
                data: {
                  clientId: selectedFolder.clientId,
                  databaseId: selectedFolder.folderId,
                  view: selectedView,
                  existingResource: item,
                },
              })
            }
            openDeleteItemModal={(...itemsId) =>
              handleOpenDatabaseItemModal({
                modalType: ResourceModalType.delete,
                data: {
                  clientId: selectedFolder.clientId,
                  databaseId: selectedFolder.folderId,
                  viewId: selectedFolder.viewId || 0,
                  itemsId,
                },
              })
            }
          />
        }
        items={(itemsToShow as IResourceDatabaseViewItemsData).Data || []}
        tableTitle={t(getSelectedFolderName(foldersList || [], selectedFolder))}
        tableTitleActionSlot={
          <DatabaseItemsTableTitleSlotContainer>
            <DatabaseItemsTableStatusLabel>
              {t(`resources.databases.viewAccess.${viewAccessType}`)}
            </DatabaseItemsTableStatusLabel>
            <ResourceDatabaseTitleAction
              label={t`resources.content.manageView`}
              dropdownConfig={{
                options: [
                  {
                    label: t`common.btnEdit`,
                    callback: () => '',
                  },
                ],
              }}
            />
          </DatabaseItemsTableTitleSlotContainer>
        }
      />
    </>
  );
};

export default ResourceViewRoute;

const DatabaseItemsTableTitleSlotContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: flex-end;
`;

const DatabaseItemsTableStatusLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #6b7280;
`;
